/**
 * Form Section Component
 *
 * Simple form to start an evaluation:
 * - Dataset name (text input)
 * - Negotiation ID (number input)
 * - Max items (optional, for testing)
 * - Start button
 */

import React from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { useEvaluationStore } from '../../stores/evaluationStore';

export const FormSection: React.FC = () => {
  const {
    datasetName,
    negotiationId,
    maxItems,
    loading,
    setDatasetName,
    setNegotiationId,
    setMaxItems,
    startRun,
  } = useEvaluationStore();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startRun();
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Start Evaluation</h2>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Dataset Name */}
          <div>
            <label htmlFor="dataset-name" className="block text-sm font-medium text-gray-700 mb-2">
              Dataset Name *
            </label>
            <InputText
              id="dataset-name"
              value={datasetName}
              onChange={(e) => setDatasetName(e.target.value)}
              placeholder="e.g., text2sql_queries"
              className="w-full"
              required
            />
            <p className="mt-1 text-xs text-gray-500">Name of Langfuse dataset</p>
          </div>

          {/* Negotiation ID */}
          <div>
            <label htmlFor="negotiation-id" className="block text-sm font-medium text-gray-700 mb-2">
              Negotiation ID *
            </label>
            <InputNumber
              id="negotiation-id"
              value={negotiationId}
              onValueChange={(e) => setNegotiationId(e.value ?? null)}
              placeholder="e.g., 1"
              className="w-full"
              required
              min={1}
            />
            <p className="mt-1 text-xs text-gray-500">Context for evaluation</p>
          </div>

          {/* Max Items */}
          <div>
            <label htmlFor="max-items" className="block text-sm font-medium text-gray-700 mb-2">
              Max Items (Optional)
            </label>
            <InputNumber
              id="max-items"
              value={maxItems}
              onValueChange={(e) => setMaxItems(e.value ?? null)}
              placeholder="e.g., 5"
              className="w-full"
              min={1}
              max={100}
            />
            <p className="mt-1 text-xs text-gray-500">Limit for testing (default: all)</p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || !datasetName || !negotiationId}
            className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
              loading || !datasetName || !negotiationId
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <span className="flex items-center">
                <i className="pi pi-spin pi-spinner mr-2" />
                Starting...
              </span>
            ) : (
              'Start Evaluation'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
